import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Download, Plus, RotateCcw, Wallet } from 'lucide-react';
import {
  FINANCIAL_ENTRY_ORIGIN_LABELS,
  FINANCIAL_ENTRY_STATUS_LABELS,
  PAYMENT_METHOD_LABELS,
  formatCents,
  formatCentsPlain,
  type FinancialEntry,
  type FinancialEntryType,
  type Paginated,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { FilterBar } from '@/components/FilterBar';
import { SmartDataTable, type Column, type SortState } from '@/components/SmartDataTable';
import { ContextPanel, ReadField, StatTiles } from '@/components/ContextPanel';
import { Attachments } from '@/components/Attachments';
import { StatusBadge, type BadgeKind } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { EmptyState, ErrorState, NoResultsState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';
import { useBusinesses } from '@/lib/queries';
import { EntryFormDrawer } from './EntryForm';
import { PayDialog, ReverseDialog, CancelEntryDialog } from './EntryActions';
import { ChargesOfInstallment } from './ChargeActions';
import { DateInput } from '@/components/DateInput';

const STATUS_TONE: Record<string, BadgeKind> = {
  OPEN: 'info',
  PARTIALLY_PAID: 'warn',
  PAID: 'ok',
  CANCELLED: 'mute',
};

/**
 * Contas a pagar (e a receber — a mesma tela, com `type` diferente).
 *
 * A linha mostra o que o operador precisa decidir: quanto falta, quando vence
 * e se já venceu. O ContextPanel abre as parcelas e o extrato de baixas, com a
 * lista atrás — dar baixa não deve custar a perda do contexto da lista.
 */
export function LancamentosPage({ type }: { type: FinancialEntryType }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<{
    installmentId: string;
    openCents: number;
    businessId: string;
    businessName: string | null;
  } | null>(null);
  const [reversing, setReversing] = useState<{ paymentId: string; amountCents: number } | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const businesses = useBusinesses();

  const listParams = {
    type,
    search: search || undefined,
    status: status || undefined,
    businessId: businessId || undefined,
    onlyOverdue: onlyOverdue ? 'true' : undefined,
    dueFrom: dueFrom || undefined,
    dueTo: dueTo || undefined,
    ...(sort ? { sort: sort.sort, direction: sort.direction } : {}),
  };

  type EntryList = Paginated<FinancialEntry> & {
    summary: { totalCents: number; openCents: number; overdueCents: number };
  };

  const listQuery = useQuery({
    queryKey: ['financial-entries', { ...listParams, page }],
    queryFn: () => api.get<EntryList>('/financial-entries', { ...listParams, page, perPage: 25 }),
  });

  const detailQuery = useQuery({
    queryKey: ['financial-entry', selectedId],
    queryFn: () => api.get<FinancialEntry>(`/financial-entries/${selectedId}`),
    enabled: Boolean(selectedId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
    void queryClient.invalidateQueries({ queryKey: ['financial-entry'] });
    void queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const payMutation = useMutation({
    mutationFn: (payload: { installmentId: string; body: unknown }) =>
      api.post<FinancialEntry>(`/installments/${payload.installmentId}/pay`, payload.body),
    onSuccess: () => {
      setPaying(null);
      refresh();
    },
  });

  const reverseMutation = useMutation({
    mutationFn: (payload: { paymentId: string; reason: string }) =>
      api.post<FinancialEntry>(`/payments/${payload.paymentId}/reverse`, {
        reason: payload.reason,
      }),
    onSuccess: () => {
      setReversing(null);
      refresh();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (reason: string) =>
      api.post<FinancialEntry>(`/financial-entries/${selectedId}/cancel`, { reason }),
    onSuccess: () => {
      setCancelling(false);
      refresh();
    },
  });

  const isPayable = type === 'PAYABLE';
  const entry = detailQuery.data;

  const columns: Array<Column<FinancialEntry>> = [
    { key: 'code', label: 'Código', mono: true, strong: true, render: (row) => row.code },
    {
      key: 'description',
      label: 'Descrição',
      render: (row) => (
        <span title={row.description}>
          {row.description.length > 44 ? `${row.description.slice(0, 44)}…` : row.description}
        </span>
      ),
    },
    {
      key: 'personName',
      label: isPayable ? 'Fornecedor' : 'Cliente',
      render: (row) => row.personName ?? '—',
    },
    { key: 'businessName', label: 'Empresa', render: (row) => row.businessName ?? '—' },
    {
      key: 'nextDueDate',
      label: 'Próx. vencimento',
      mono: true,
      render: (row) =>
        row.nextDueDate ? (
          <span style={row.isOverdue ? { color: T.red, fontWeight: 600 } : undefined}>
            {formatDate(row.nextDueDate)}
          </span>
        ) : (
          '—'
        ),
    },
    {
      key: 'totalCents',
      label: 'Valor',
      right: true,
      mono: true,
      sortable: true,
      render: (row) => formatCents(row.totalCents),
    },
    {
      key: 'openCents',
      label: 'Em aberto',
      right: true,
      mono: true,
      // Âmbar era pintado em TODA linha com saldo — ou seja, em quase todas.
      // Cor que aparece sempre deixa de avisar: a lista inteira ficava alerta
      // e o que de fato estourou não se distinguia do que vence mês que vem.
      // Atenção fica para o vencido, que é onde o Diretor precisa olhar.
      render: (row) => (
        <span
          style={
            row.openCents > 0
              ? { color: row.isOverdue ? T.red : undefined, fontWeight: 600 }
              : undefined
          }
        >
          {row.openCents > 0 ? formatCents(row.openCents) : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (row) =>
        row.isOverdue && row.status !== 'CANCELLED' ? (
          <StatusBadge kind="bad">Vencido</StatusBadge>
        ) : (
          <StatusBadge kind={STATUS_TONE[row.status] ?? 'mute'}>
            {FINANCIAL_ENTRY_STATUS_LABELS[row.status]}
          </StatusBadge>
        ),
    },
  ];

  const hasFilters = Boolean(search || status || businessId || onlyOverdue || dueFrom || dueTo);

  return (
    <>
      <WorkspaceHeader
        title={isPayable ? 'Contas a pagar' : 'Contas a receber'}
        actions={
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {can('lancamentos:exportar') && (
              <Button
                icon={Download}
                onClick={() => {
                  void api
                    .get<EntryList>('/financial-entries', { ...listParams, page: 1, perPage: 500 })
                    .then((result) => {
                      downloadCsv(isPayable ? 'contas-a-pagar' : 'contas-a-receber', [
                        ['Código', 'Descrição', isPayable ? 'Fornecedor' : 'Cliente', 'Empresa', 'Centro de custo', 'Origem', 'Competência', 'Próx. vencimento', 'Valor', 'Em aberto', 'Situação'],
                        ...result.data.map((row) => [
                          row.code,
                          row.description,
                          row.personName ?? '',
                          row.businessName ?? '',
                          row.costCenterName ?? '',
                          FINANCIAL_ENTRY_ORIGIN_LABELS[row.origin],
                          formatDate(row.competenceDate),
                          row.nextDueDate ? formatDate(row.nextDueDate) : '',
                          formatCentsPlain(row.totalCents),
                          formatCentsPlain(row.openCents),
                          FINANCIAL_ENTRY_STATUS_LABELS[row.status],
                        ]),
                      ]);
                    });
                }}
              >
                Exportar CSV
              </Button>
            )}
            {can('lancamentos:criar') && (
              <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                Novo lançamento
              </Button>
            )}
          </span>
        }
      />

      <div className="workspace__body">
        <FilterBar
          placeholder="Buscar por código, descrição, documento ou fornecedor…"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filters={[
            {
              key: 'status',
              label: 'Situação',
              value: status,
              options: Object.entries(FINANCIAL_ENTRY_STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              })),
              onChange: (value) => {
                setStatus(value);
                setPage(1);
              },
            },
            {
              key: 'business',
              label: 'Empresa',
              value: businessId,
              options: (businesses.data ?? []).map((b) => ({ value: b.id, label: b.name })),
              onChange: (value) => {
                setBusinessId(value);
                setPage(1);
              },
            },
          ]}
        >
          <button
            type="button"
            className={`filterbar__chip${onlyOverdue ? ' is-active' : ''}`}
            onClick={() => {
              setOnlyOverdue((v) => !v);
              setPage(1);
            }}
          >
            <AlertTriangle size={12} aria-hidden /> Só vencidos
          </button>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
            <span className="muted">Vence de</span>
            <DateInput
              className="input"
              aria-label="Vencimento a partir de"
              value={dueFrom}
              onChange={(iso) => {
                setDueFrom(iso);
                setPage(1);
              }}
              style={{ width: 140 }}
            />
            <span className="muted">até</span>
            <DateInput
              className="input"
              aria-label="Vencimento até"
              value={dueTo}
              onChange={(iso) => {
                setDueTo(iso);
                setPage(1);
              }}
              style={{ width: 140 }}
            />
          </label>
        </FilterBar>

        {listQuery.data && (
          <div className="metrics" style={{ marginBottom: 14 }}>
            <div className="metric">
              <div className="metric__head">
                <span className="metric__label">Total do filtro</span>
              </div>
              <div className="metric__value">{formatCents(listQuery.data.summary.totalCents)}</div>
            </div>
            <div className="metric">
              <div className="metric__head">
                <span className="metric__label">Em aberto</span>
              </div>
              <div className="metric__value">{formatCents(listQuery.data.summary.openCents)}</div>
            </div>
            <div className="metric">
              <div className="metric__head">
                <span className="metric__label">Vencido</span>
              </div>
              <div
                className={`metric__value${listQuery.data.summary.overdueCents > 0 ? ' metric__value--bad' : ''}`}
              >
                {formatCents(listQuery.data.summary.overdueCents)}
              </div>
            </div>
          </div>
        )}

        {listQuery.isPending && <TableSkeleton columns={8} />}
        {listQuery.isError && (
          <ErrorState
            message={errorMessage(listQuery.error)}
            onRetry={() => void listQuery.refetch()}
          />
        )}
        {listQuery.data && (
          <SmartDataTable
            caption={isPayable ? 'Contas a pagar' : 'Contas a receber'}
            columns={columns}
            rows={listQuery.data.data}
            meta={listQuery.data.meta}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
            onPageChange={setPage}
            sortState={sort ?? undefined}
            onSortChange={(next) => {
              setSort(next);
              setPage(1);
            }}
            emptyState={
              hasFilters ? (
                <NoResultsState
                  onClear={() => {
                    setSearch('');
                    setStatus('');
                    setBusinessId('');
                    setOnlyOverdue(false);
                    setDueFrom('');
                    setDueTo('');
                  }}
                />
              ) : (
                <EmptyState
                  title={isPayable ? 'Nenhuma conta a pagar' : 'Nenhuma conta a receber'}
                  description="Lance a primeira conta ou importe uma NF-e na Central de Documentos."
                  action={
                    can('lancamentos:criar') ? (
                      <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
                        Novo lançamento
                      </Button>
                    ) : undefined
                  }
                />
              )
            }
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        title={entry ? `${entry.code} · ${entry.description}` : 'Carregando…'}
        subtitle={entry?.personName ?? undefined}
        tabs={['Parcelas', 'Dados', 'Extrato', 'Anexos']}
        footer={
          entry && entry.status !== 'CANCELLED' && can('lancamentos:excluir') ? (
            <Button variant="danger" icon={Ban} small onClick={() => setCancelling(true)}>
              Cancelar lançamento
            </Button>
          ) : undefined
        }
      >
        {(tab) => {
          if (detailQuery.isPending) return <p className="muted">Carregando…</p>;
          if (!entry) return <p className="muted">Não foi possível carregar.</p>;

          if (tab === 0) {
            return (
              <>
                <StatTiles
                  items={[
                    { label: 'Total', value: formatCents(entry.totalCents) },
                    { label: 'Em aberto', value: formatCents(entry.openCents) },
                  ]}
                />
                {entry.installments.map((installment) => (
                  <div
                    key={installment.id}
                    className="card"
                    style={{ padding: 12, marginBottom: 10 }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          Parcela {installment.number}/{entry.installments.length}
                        </div>
                        <div className="mono" style={{ color: T.inkFaint, marginTop: 2 }}>
                          vence {formatDate(installment.dueDate)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="mono" style={{ fontWeight: 600 }}>
                          {formatCents(installment.amountCents)}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          {installment.status === 'PAID' ? (
                            <StatusBadge kind="ok">Pago</StatusBadge>
                          ) : installment.isOverdue ? (
                            <StatusBadge kind="bad">Vencido</StatusBadge>
                          ) : installment.status === 'PARTIALLY_PAID' ? (
                            <StatusBadge kind="warn">
                              Falta {formatCents(installment.openCents)}
                            </StatusBadge>
                          ) : (
                            <StatusBadge kind="info">Em aberto</StatusBadge>
                          )}
                        </div>
                      </div>
                    </div>

                    {installment.openCents > 0 &&
                      entry.status !== 'CANCELLED' &&
                      can('lancamentos:aprovar') && (
                        <div style={{ marginTop: 10 }}>
                          <Button
                            small
                            variant="primary"
                            icon={Wallet}
                            onClick={() =>
                              setPaying({
                                installmentId: installment.id,
                                openCents: installment.openCents,
                                businessId: entry.businessId,
                                businessName: entry.businessName,
                              })
                            }
                          >
                            Dar baixa
                          </Button>
                        </div>
                      )}

                    {/* Só conta a RECEBER cobra: mandar o ASAAS pedir dinheiro
                        a quem a gente deve não faz sentido nenhum. */}
                    {!isPayable && can('cobrancas:ver') && (
                      <ChargesOfInstallment
                        installmentId={installment.id}
                        openCents={installment.openCents}
                        canCharge={entry.status !== 'CANCELLED'}
                        onChanged={refresh}
                      />
                    )}
                  </div>
                ))}
              </>
            );
          }

          if (tab === 1) {
            return (
              <>
                <ReadField label="Descrição" value={entry.description} />
                <ReadField label={isPayable ? 'Fornecedor' : 'Cliente'} value={entry.personName ?? '—'} />
                <ReadField label="Documento" value={entry.documentNumber ?? '—'} mono />
                <ReadField label="Emissão" value={formatDate(entry.issueDate)} mono />
                <ReadField label="Competência" value={formatDate(entry.competenceDate)} mono />
                <ReadField label="Empresa" value={entry.businessName ?? '—'} />
                <ReadField label="Centro de custo" value={entry.costCenterName ?? '—'} />
                <ReadField label="Conta" value={entry.chartAccountName ?? '—'} />
                <ReadField label="Origem" value={FINANCIAL_ENTRY_ORIGIN_LABELS[entry.origin]} />
                {entry.notes && <ReadField label="Observações" value={entry.notes} />}
                {entry.cancelReason && (
                  <ReadField label="Motivo do cancelamento" value={entry.cancelReason} />
                )}
              </>
            );
          }

          if (tab === 3) {
            return (
              <Attachments
                entity="FinancialEntry"
                entityId={entry.id}
                canEdit={can('lancamentos:editar')}
              />
            );
          }

          const payments = entry.installments.flatMap((installment) =>
            installment.payments.map((payment) => ({ payment, installment })),
          );

          if (payments.length === 0) {
            return <p className="muted" style={{ fontSize: 13 }}>Nenhuma baixa registrada ainda.</p>;
          }

          return (
            <>
              {payments.map(({ payment, installment }) => (
                <div key={payment.id} className="card" style={{ padding: 12, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {payment.isReversal ? 'Estorno' : 'Baixa'} · parcela {installment.number}
                      </div>
                      <div className="mono" style={{ color: T.inkFaint, marginTop: 2 }}>
                        {formatDate(payment.paidAt)} · {PAYMENT_METHOD_LABELS[payment.method]}
                      </div>
                      {payment.bankAccountName && (
                        <div style={{ fontSize: 12, color: T.inkFaint }}>
                          {payment.bankAccountName}
                          {payment.createdByName ? ` · por ${payment.createdByName}` : ''}
                        </div>
                      )}
                      {payment.reason && (
                        <div style={{ fontSize: 12, color: T.amber, marginTop: 4 }}>
                          {payment.reason}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        className="mono"
                        style={{ fontWeight: 600, color: payment.isReversal ? T.red : T.green }}
                      >
                        {formatCents(payment.amountCents)}
                      </div>
                      {!payment.isReversal && can('lancamentos:aprovar') && (
                        <Button
                          small
                          variant="ghost"
                          icon={RotateCcw}
                          onClick={() =>
                            setReversing({
                              paymentId: payment.id,
                              amountCents: payment.amountCents,
                            })
                          }
                        >
                          Estornar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          );
        }}
      </ContextPanel>

      <EntryFormDrawer
        open={creating}
        type={type}
        canAttach={can('lancamentos:editar')}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          refresh();
        }}
      />

      <PayDialog
        open={Boolean(paying)}
        openCents={paying?.openCents ?? 0}
        businessId={paying?.businessId ?? null}
        businessName={paying?.businessName ?? null}
        loading={payMutation.isPending}
        error={payMutation.error}
        onClose={() => {
          setPaying(null);
          payMutation.reset();
        }}
        onConfirm={(body) =>
          paying && payMutation.mutate({ installmentId: paying.installmentId, body })
        }
      />

      <ReverseDialog
        open={Boolean(reversing)}
        amountCents={reversing?.amountCents ?? 0}
        loading={reverseMutation.isPending}
        error={reverseMutation.error}
        onClose={() => {
          setReversing(null);
          reverseMutation.reset();
        }}
        onConfirm={(reason) =>
          reversing && reverseMutation.mutate({ paymentId: reversing.paymentId, reason })
        }
      />

      <CancelEntryDialog
        open={cancelling}
        loading={cancelMutation.isPending}
        error={cancelMutation.error}
        onClose={() => {
          setCancelling(false);
          cancelMutation.reset();
        }}
        onConfirm={(reason) => cancelMutation.mutate(reason)}
      />
    </>
  );
}
